const marks = [
  {
    title: "The work begins to want itself.",
    body: "You stop dragging yourself to the page. Something on the other side starts pulling, gently. Days organise around the work, not against it.",
  },
  {
    title: "Time stops feeling stolen.",
    body: "Hours you used to lose to scrolling or numbing find their way back. Not all of them, not at once, but enough that you notice the difference.",
  },
  {
    title: "Your truth becomes easier to say.",
    body: "Not louder. Just less buried. The yeses start sounding like yours. The nos arrive without apology. You stop translating yourself for the room.",
  },
  {
    title: "You stop performing creativity.",
    body: "Practice replaces performance. The work doesn&rsquo;t need to be impressive to count. It just needs to be honest, today, in this small window.",
  },
  {
    title: "You remember which life is yours.",
    body: "The one you can feel under all the should&rsquo;ves. The one that&rsquo;s been quietly waiting for you to come back. The one that&rsquo;s still completely possible.",
  },
];

export default function HowItWorks() {
  return (
    <section
      id="process"
      data-testid="how-it-works"
      className="bg-[#F7F5F2] border-b border-[#E5E0D8]"
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 md:col-span-7" data-reveal>
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#1FA5B8] font-body">
              Field marks of awakening
            </div>
            <h2 className="mt-4 font-display text-[#1A1A1A] tracking-tight leading-[1.02] text-[clamp(2.25rem,5vw,4rem)] font-medium">
              What changes,{" "}
              <span className="italic font-light">slowly</span>, when you
              begin again.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9" data-reveal>
            <p className="font-body text-[#5C5C5C] text-base md:text-lg leading-relaxed">
              We don&apos;t talk about the doorway here. We talk about what
              lives on the other side of it, so you remember what you&apos;re
              walking toward.
            </p>
          </div>
        </div>

        <div className="mt-16 md:mt-24 grid grid-cols-12 gap-x-6 gap-y-12">
          {marks.map((m, idx) => {
            const spans = [
              "col-span-12 md:col-span-5",
              "col-span-12 md:col-span-6 md:col-start-7",
              "col-span-12 md:col-span-5",
              "col-span-12 md:col-span-7 md:col-start-6",
              "col-span-12 md:col-span-6",
            ];
            return (
              <article
                key={idx}
                data-testid={`awakening-mark-${idx + 1}`}
                data-reveal
                className={`${spans[idx]} border-t border-[#1A1A1A] pt-6 pr-2`}
              >
                <div className="flex items-baseline justify-between">
                  <CelestialDot index={idx} />
                  <span className="text-[10px] uppercase tracking-[0.3em] text-[#5C5C5C] font-body">
                    Field mark · {String(idx + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3
                  className="mt-5 font-display text-[28px] md:text-[32px] leading-tight text-[#1A1A1A] tracking-tight"
                  dangerouslySetInnerHTML={{ __html: m.title }}
                />
                <p
                  className="mt-3 font-body text-[#5C5C5C] leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: m.body }}
                />
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CelestialDot({ index }) {
  // Different small celestial marks for each field mark
  const variants = [
    // crescent
    <svg key="0" width="28" height="28" viewBox="0 0 28 28" aria-hidden="true">
      <circle cx="14" cy="14" r="9" stroke="#1FA5B8" strokeWidth="1" fill="none" />
      <circle cx="17" cy="11" r="7" fill="#F7F5F2" />
    </svg>,
    // sun rays
    <svg key="1" width="28" height="28" viewBox="0 0 28 28" aria-hidden="true">
      <circle cx="14" cy="14" r="4" fill="#1FA5B8" />
      {[...Array(8)].map((_, i) => {
        const a = (i * 45 * Math.PI) / 180;
        return (
          <line
            key={i}
            x1={14 + Math.cos(a) * 7}
            y1={14 + Math.sin(a) * 7}
            x2={14 + Math.cos(a) * 11}
            y2={14 + Math.sin(a) * 11}
            stroke="#1FA5B8"
            strokeWidth="1"
          />
        );
      })}
    </svg>,
    // open circle
    <svg key="2" width="28" height="28" viewBox="0 0 28 28" aria-hidden="true">
      <circle cx="14" cy="14" r="11" stroke="#1FA5B8" strokeWidth="0.8" fill="none" />
      <circle cx="14" cy="14" r="2" fill="#1FA5B8" />
    </svg>,
    // diamond
    <svg key="3" width="28" height="28" viewBox="0 0 28 28" aria-hidden="true">
      <polygon points="14,3 25,14 14,25 3,14" stroke="#1FA5B8" strokeWidth="0.8" fill="none" />
      <circle cx="14" cy="14" r="1.6" fill="#1FA5B8" />
    </svg>,
    // dotted orbit
    <svg key="4" width="28" height="28" viewBox="0 0 28 28" aria-hidden="true">
      <circle cx="14" cy="14" r="10" stroke="#1FA5B8" strokeWidth="0.8" strokeDasharray="1.4 2" fill="none" />
      <circle cx="22" cy="14" r="1.8" fill="#1FA5B8" />
    </svg>,
  ];
  return variants[index] || variants[0];
}
